import { Flask } from '@mynaui/icons-react';
import { useTranslation } from 'react-i18next';

import { AnalysisMenuPopover } from '@/components/analyses/AnalysisMenuPopover';
import { HEADER_ICON_BUTTON_CLASS, type iAction } from '@/layout/Skeleton';
import { cn } from '@/lib/utils';

import { ANALYSES } from './registry';
import { countActiveRuns } from './runs';
import { useAnalysisAvailability } from './useAnalysisAvailability';
import { useAnalysisRuns } from './useAnalysisRuns';
import { useCanRunAnalyses } from './useCanRunAnalyses';

/**
 * The project-header entry (spec amendments A2/A3): ONE icon button that
 * opens the anchored analysis dropdown — it replaced the bar-chart "Analyses"
 * icon and the "Run analysis…" / "Generate data…" text buttons. The badge
 * pulses while a run is in flight.
 *
 * It is an `element` action because the popover needs to own the trigger;
 * the trigger reuses the skeleton's `HEADER_ICON_BUTTON_CLASS` and puts the
 * icon as a DIRECT `svg` child, so `[&>svg]:size-4` sizes it exactly like
 * Share / Edit / Upload / Layers beside it (A3 — the old icon was wrapped in
 * a div, escaped that selector and rendered at lucide's 24 px).
 *
 * Hidden for non-editors / shared links, and when no analysis is visible in
 * either mode (a dropdown that opens empty reads as a bug).
 *
 * The badge counts the same `analysisRuns` the dropdown footer's "N running"
 * counts (every analysis type, batch-derived status, stuck runs ignored) —
 * same query key, so one request serves both. "Now" is the moment the data
 * was fetched (`dataUpdatedAt`): fresh on every poll, and pure at render.
 */
export function useAnalysisHeaderActions(projectId: string): iAction[] {
  const { t } = useTranslation('project');
  const canRun = useCanRunAnalyses();
  const availability = useAnalysisAvailability();
  // Non-editors never see the button, so they never poll for it.
  const { data: runs, dataUpdatedAt } = useAnalysisRuns(canRun ? projectId : null);
  const runningCount = countActiveRuns(runs, dataUpdatedAt);

  if (!canRun) return [];
  if (!ANALYSES.some((d) => availability(d.id).visible)) return [];

  const label = t('viewer.openAnalyses');
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const mod = isMac ? '⌘' : 'Ctrl';
  return [
    {
      element: (
        <AnalysisMenuPopover projectId={projectId} hotkey='Mod+Alt+A'>
          <button
            type='button'
            data-testid='header-analyses'
            aria-label={label}
            className={cn(HEADER_ICON_BUTTON_CLASS, 'relative')}
          >
            <Flask />
            {runningCount > 0 && (
              <span
                data-testid='header-analyses-running'
                className='absolute top-0.5 right-0.5 flex size-2 items-center justify-center rounded-full bg-blue-500'
              >
                <span className='absolute size-2 animate-ping rounded-full bg-blue-400 opacity-75' />
              </span>
            )}
          </button>
        </AnalysisMenuPopover>
      ),
      tooltip: label,
      shortcutKey: `${mod}+${isMac ? '⌥' : 'Alt'}+A`,
    },
  ];
}
